import type { PrismaClient } from '@prisma/client';
import { Router, type Request, type Response } from 'express';
import rateLimit from 'express-rate-limit';
import { authenticator } from 'otplib';
import QRCode from 'qrcode';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { decrypt, encrypt, randomToken, sha256 } from '../../core/crypto.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, parse } from '../../core/http.js';
import type { Logger } from '../../core/logger.js';
import { queueEmail, renderTemplate } from '../messaging/messaging.service.js';
import { authenticate } from './auth.middleware.js';
import { assertStrongPassword, dummyHash, hashPassword, verifyPassword } from './password.js';
import {
  ACCESS_TTL_SECONDS,
  REFRESH_TTL_MS,
  issueRefreshToken,
  revokeAllSessions,
  rotateRefreshToken,
  signAccessToken,
} from './tokens.js';

interface Deps {
  db: PrismaClient;
  config: Config;
  logger: Logger;
}

const COOKIE = 'rt';
const GENERIC_LOGIN_ERROR = 'Invalid email or password';
const RESET_TTL_MS = 60 * 60 * 1000;
const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

authenticator.options = { window: 1 };

const clientOf = (req: Request): 'web' | 'mobile' =>
  req.header('x-client')?.toLowerCase() === 'mobile' ? 'mobile' : 'web';

export function createAuthRouter({ db, config, logger }: Deps) {
  const router = Router();
  const isProd = config.NODE_ENV === 'production';
  const requireAuth = authenticate({ db, config });
  const requireAuthAllowUnenrolled = authenticate({ db, config }, { allowUnenrolled: true });

  const loginLimiter = rateLimit({
    windowMs: 15 * 60_000,
    limit: config.NODE_ENV === 'test' ? 1000 : config.AUTH_RATE_LIMIT,
    standardHeaders: 'draft-7',
    legacyHeaders: false,
    message: { error: { code: 'RATE_LIMITED', message: 'Too many attempts. Try again later.' } },
  });
  const resetLimiter = rateLimit({
    windowMs: 60 * 60_000,
    limit: config.NODE_ENV === 'test' ? 1000 : 10,
    standardHeaders: 'draft-7',
    legacyHeaders: false,
    message: { error: { code: 'RATE_LIMITED', message: 'Too many requests. Try again later.' } },
  });

  function setRefreshCookie(res: Response, token: string) {
    res.cookie(COOKIE, token, {
      httpOnly: true,
      secure: isProd,
      sameSite: 'strict',
      path: '/api/v1/auth',
      maxAge: REFRESH_TTL_MS,
    });
  }
  const clearRefreshCookie = (res: Response) =>
    res.clearCookie(COOKIE, {
      httpOnly: true,
      secure: isProd,
      sameSite: 'strict',
      path: '/api/v1/auth',
    });

  /** Cookie-authenticated POSTs must come from an allowed origin (defence in depth beside SameSite). */
  function assertSameOrigin(req: Request) {
    const origin = req.header('origin');
    if (origin && !config.CORS_ORIGINS.includes(origin)) {
      throw new HttpError(403, 'BAD_ORIGIN', 'Request origin not allowed');
    }
  }

  async function startSession(
    req: Request,
    res: Response,
    user: { id: number; companyId: number },
    deviceLabel?: string,
  ) {
    const client = clientOf(req);
    const { token } = await issueRefreshToken(db, user.id, {
      client,
      ip: clientIp(req),
      deviceLabel: deviceLabel ?? req.header('user-agent')?.slice(0, 200),
    });
    const accessToken = await signAccessToken(config, {
      userId: user.id,
      companyId: user.companyId,
    });
    if (client === 'web') setRefreshCookie(res, token);
    return {
      accessToken,
      expiresIn: ACCESS_TTL_SECONDS,
      // Native apps keep this in the OS secure store; browsers only get the httpOnly cookie.
      ...(client === 'mobile' ? { refreshToken: token } : {}),
    };
  }

  async function mePayload(userId: number, companyId: number) {
    const { loadAuthContext } = await import('./auth.middleware.js');
    const ctx = await loadAuthContext(db, userId, companyId);
    if (!ctx) throw new HttpError(401, 'UNAUTHENTICATED', 'Session expired');
    const company = await db.company.findUnique({ where: { id: companyId } });
    return {
      id: ctx.userId,
      name: ctx.name,
      email: ctx.email,
      roles: ctx.roleNames,
      permissions: ctx.permissions,
      scope: ctx.scope,
      approvalLimit: ctx.approvalLimit,
      requiresTwoFactor: ctx.requiresTwoFactor,
      twoFactorEnabled: ctx.twoFactorEnabled,
      needsTwoFactorSetup: ctx.requiresTwoFactor && !ctx.twoFactorEnabled,
      company: company && {
        id: company.id,
        name: company.name,
        baseCurrency: company.baseCurrency,
        brandColor: company.brandColor,
      },
    };
  }

  // ───── login ─────
  const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(191),
    password: z.string().min(1).max(200),
    totp: z
      .string()
      .trim()
      .regex(/^\d{6}$/)
      .optional(),
    recoveryCode: z.string().trim().min(6).max(40).optional(),
    deviceLabel: z.string().max(200).optional(),
  });

  router.post(
    '/login',
    loginLimiter,
    asyncHandler(async (req, res) => {
      const body = parse(loginSchema, req.body);
      const ip = clientIp(req);
      const user = await db.user.findFirst({
        where: { email: body.email },
        include: { totp: true },
      });

      if (!user || user.status !== 'active' || !user.passwordHash) {
        await verifyPassword(await dummyHash(), body.password); // equalise timing
        logger.warn({ ip }, 'Login failed: unknown or inactive account');
        throw new HttpError(401, 'INVALID_CREDENTIALS', GENERIC_LOGIN_ERROR);
      }

      const now = new Date();
      if (user.lockedUntil && user.lockedUntil > now) {
        // Same message as a wrong password so lockout does not reveal the account exists.
        throw new HttpError(401, 'INVALID_CREDENTIALS', GENERIC_LOGIN_ERROR);
      }

      const fail = async (reason: string) => {
        const failed = user.failedLogins + 1;
        const lockMinutes = failed >= 5 ? Math.min(2 ** (failed - 5), 60) : 0;
        await db.user.update({
          where: { id: user.id },
          data: {
            failedLogins: failed,
            lockedUntil: lockMinutes ? new Date(now.getTime() + lockMinutes * 60_000) : null,
          },
        });
        await audit(
          db,
          { companyId: user.companyId, userId: user.id, ip },
          {
            action: 'auth.login_failed',
            entity: 'user',
            entityId: user.id,
            after: { reason, failedLogins: failed, lockedMinutes: lockMinutes },
          },
        );
        throw new HttpError(401, 'INVALID_CREDENTIALS', GENERIC_LOGIN_ERROR);
      };

      if (!(await verifyPassword(user.passwordHash, body.password))) return fail('bad_password');

      if (user.twoFactorEnabled && user.totp?.confirmedAt) {
        if (!body.totp && !body.recoveryCode) {
          return res.json({ requires2fa: true });
        }
        let secondFactorOk = false;
        if (body.totp) {
          secondFactorOk = authenticator.check(
            body.totp,
            decrypt(user.totp.secretEnc, config.ENCRYPTION_KEY),
          );
        } else if (body.recoveryCode) {
          const codes = (user.totp.recoveryCodes as { hash: string; used: boolean }[] | null) ?? [];
          const hash = sha256(body.recoveryCode.replace(/\s/g, '').toLowerCase());
          const match = codes.find((c) => c.hash === hash && !c.used);
          if (match) {
            match.used = true;
            await db.userTotp.update({
              where: { userId: user.id },
              data: { recoveryCodes: codes },
            });
            secondFactorOk = true;
          }
        }
        if (!secondFactorOk) return fail('bad_second_factor');
      }

      await db.user.update({
        where: { id: user.id },
        data: { failedLogins: 0, lockedUntil: null, lastLoginAt: now, lastLoginIp: ip },
      });
      await audit(
        db,
        { companyId: user.companyId, userId: user.id, ip },
        {
          action: 'auth.login',
          entity: 'user',
          entityId: user.id,
        },
      );
      const session = await startSession(req, res, user, body.deviceLabel);
      res.json({ ...session, user: await mePayload(user.id, user.companyId) });
    }),
  );

  // ───── refresh / logout ─────
  router.post(
    '/refresh',
    asyncHandler(async (req, res) => {
      const client = clientOf(req);
      if (client === 'web') assertSameOrigin(req);
      const presented: unknown =
        client === 'mobile' ? req.body?.refreshToken : req.cookies?.[COOKIE];
      if (typeof presented !== 'string' || presented.length < 20) {
        throw new HttpError(401, 'UNAUTHENTICATED', 'Sign in required');
      }
      const result = await rotateRefreshToken(db, presented, { client, ip: clientIp(req) });
      if (!result.ok) {
        if (client === 'web') clearRefreshCookie(res);
        throw new HttpError(401, 'UNAUTHENTICATED', 'Session expired');
      }
      const user = await db.user.findUnique({ where: { id: result.userId } });
      if (!user || user.status !== 'active') {
        await revokeAllSessions(db, result.userId);
        if (client === 'web') clearRefreshCookie(res);
        throw new HttpError(401, 'UNAUTHENTICATED', 'Session expired');
      }
      const accessToken = await signAccessToken(config, {
        userId: user.id,
        companyId: user.companyId,
      });
      if (client === 'web') setRefreshCookie(res, result.token);
      res.json({
        accessToken,
        expiresIn: ACCESS_TTL_SECONDS,
        ...(client === 'mobile' ? { refreshToken: result.token } : {}),
        user: await mePayload(user.id, user.companyId),
      });
    }),
  );

  router.post(
    '/logout',
    asyncHandler(async (req, res) => {
      const client = clientOf(req);
      if (client === 'web') assertSameOrigin(req);
      const presented: unknown =
        client === 'mobile' ? req.body?.refreshToken : req.cookies?.[COOKIE];
      if (typeof presented === 'string') {
        const row = await db.refreshToken.findUnique({ where: { tokenHash: sha256(presented) } });
        if (row) {
          await db.refreshToken.updateMany({
            where: { familyId: row.familyId, revokedAt: null },
            data: { revokedAt: new Date() },
          });
        }
      }
      if (client === 'web') clearRefreshCookie(res);
      res.status(204).end();
    }),
  );

  router.post(
    '/logout-all',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      await revokeAllSessions(db, req.auth!.userId);
      await audit(
        db,
        { companyId: req.auth!.companyId, userId: req.auth!.userId, ip: clientIp(req) },
        {
          action: 'auth.logout_all',
          entity: 'user',
          entityId: req.auth!.userId,
        },
      );
      clearRefreshCookie(res);
      res.status(204).end();
    }),
  );

  router.get(
    '/me',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      res.json(await mePayload(req.auth!.userId, req.auth!.companyId));
    }),
  );

  // ───── sessions ─────
  router.get(
    '/sessions',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      const rows = await db.refreshToken.findMany({
        where: { userId: req.auth!.userId, revokedAt: null, expiresAt: { gt: new Date() } },
        orderBy: { createdAt: 'desc' },
      });
      res.json({
        items: rows.map((r) => ({
          id: r.id,
          client: r.client,
          device: r.deviceLabel,
          ip: r.ip,
          createdAt: r.createdAt,
          lastUsedAt: r.lastUsedAt,
        })),
      });
    }),
  );

  router.delete(
    '/sessions/:id',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const row = await db.refreshToken.findFirst({ where: { id, userId: req.auth!.userId } });
      if (!row) throw new HttpError(404, 'NOT_FOUND', 'Session not found');
      await db.refreshToken.updateMany({
        where: { familyId: row.familyId, revokedAt: null },
        data: { revokedAt: new Date() },
      });
      res.status(204).end();
    }),
  );

  // ───── two-factor ─────
  router.post(
    '/2fa/setup',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      const auth = req.auth!;
      if (auth.twoFactorEnabled)
        throw new HttpError(409, 'ALREADY_ENABLED', 'Two-factor is already enabled');
      const secret = authenticator.generateSecret();
      await db.userTotp.upsert({
        where: { userId: auth.userId },
        create: { userId: auth.userId, secretEnc: encrypt(secret, config.ENCRYPTION_KEY) },
        update: {
          secretEnc: encrypt(secret, config.ENCRYPTION_KEY),
          confirmedAt: null,
          recoveryCodes: undefined,
        },
      });
      const otpauthUrl = authenticator.keyuri(auth.email, 'Roovers Business Suite', secret);
      res.json({
        secret,
        otpauthUrl,
        qrDataUrl: await QRCode.toDataURL(otpauthUrl, { margin: 1, width: 240 }),
      });
    }),
  );

  router.post(
    '/2fa/confirm',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      const auth = req.auth!;
      const { code } = parse(
        z.object({
          code: z
            .string()
            .trim()
            .regex(/^\d{6}$/),
        }),
        req.body,
      );
      const totp = await db.userTotp.findUnique({ where: { userId: auth.userId } });
      if (!totp || totp.confirmedAt)
        throw new HttpError(409, 'NOT_PENDING', 'Start two-factor setup first');
      if (!authenticator.check(code, decrypt(totp.secretEnc, config.ENCRYPTION_KEY))) {
        throw new HttpError(
          400,
          'INVALID_CODE',
          'That code is not valid. Check the time on your phone and try again.',
        );
      }
      const plainCodes = Array.from({ length: 8 }, () =>
        `${randomToken(4)}-${randomToken(4)}`.toLowerCase(),
      );
      await db.$transaction(async (tx) => {
        await tx.userTotp.update({
          where: { userId: auth.userId },
          data: {
            confirmedAt: new Date(),
            recoveryCodes: plainCodes.map((c) => ({ hash: sha256(c), used: false })),
          },
        });
        await tx.user.update({ where: { id: auth.userId }, data: { twoFactorEnabled: true } });
        await audit(
          tx,
          { companyId: auth.companyId, userId: auth.userId, ip: clientIp(req) },
          {
            action: 'auth.2fa_enabled',
            entity: 'user',
            entityId: auth.userId,
          },
        );
      });
      // Shown exactly once.
      res.json({ recoveryCodes: plainCodes });
    }),
  );

  router.post(
    '/2fa/disable',
    requireAuth,
    asyncHandler(async (req, res) => {
      const auth = req.auth!;
      if (auth.requiresTwoFactor) {
        throw new HttpError(
          403,
          'TWO_FACTOR_REQUIRED',
          'Your role requires two-factor authentication',
        );
      }
      const { password } = parse(z.object({ password: z.string().min(1) }), req.body);
      const user = await db.user.findUniqueOrThrow({ where: { id: auth.userId } });
      if (!user.passwordHash || !(await verifyPassword(user.passwordHash, password))) {
        throw new HttpError(400, 'INVALID_PASSWORD', 'Password is incorrect');
      }
      await db.$transaction(async (tx) => {
        await tx.userTotp.deleteMany({ where: { userId: auth.userId } });
        await tx.user.update({ where: { id: auth.userId }, data: { twoFactorEnabled: false } });
        await audit(
          tx,
          { companyId: auth.companyId, userId: auth.userId, ip: clientIp(req) },
          {
            action: 'auth.2fa_disabled',
            entity: 'user',
            entityId: auth.userId,
          },
        );
      });
      res.status(204).end();
    }),
  );

  // ───── passwords ─────
  const passwordSchema = z.string().min(1).max(200);

  router.post(
    '/password/change',
    requireAuthAllowUnenrolled,
    asyncHandler(async (req, res) => {
      const auth = req.auth!;
      const body = parse(
        z.object({ currentPassword: passwordSchema, newPassword: passwordSchema }),
        req.body,
      );
      const user = await db.user.findUniqueOrThrow({ where: { id: auth.userId } });
      if (!user.passwordHash || !(await verifyPassword(user.passwordHash, body.currentPassword))) {
        throw new HttpError(400, 'INVALID_PASSWORD', 'Current password is incorrect');
      }
      assertStrongPassword(body.newPassword, { email: user.email, name: user.name });
      await db.user.update({
        where: { id: user.id },
        data: { passwordHash: await hashPassword(body.newPassword) },
      });
      await revokeAllSessions(db, user.id);
      await audit(
        db,
        { companyId: user.companyId, userId: user.id, ip: clientIp(req) },
        {
          action: 'auth.password_changed',
          entity: 'user',
          entityId: user.id,
        },
      );
      clearRefreshCookie(res);
      res.status(204).end();
    }),
  );

  router.post(
    '/password/forgot',
    resetLimiter,
    asyncHandler(async (req, res) => {
      const { email } = parse(
        z.object({ email: z.string().trim().toLowerCase().email() }),
        req.body,
      );
      const user = await db.user.findFirst({ where: { email, status: 'active' } });
      if (user) {
        const token = randomToken(32);
        await db.userToken.create({
          data: {
            userId: user.id,
            type: 'reset',
            tokenHash: sha256(token),
            expiresAt: new Date(Date.now() + RESET_TTL_MS),
          },
        });
        const company = await db.company.findUniqueOrThrow({ where: { id: user.companyId } });
        const link = `${config.APP_URL}/reset-password?token=${token}`;
        await queueEmail(db, {
          companyId: user.companyId,
          to: [user.email],
          subject: 'Reset your password',
          text: renderTemplate(
            'Hello {{user_name}},\n\nUse this link to choose a new password (valid for 1 hour):\n{{reset_link}}\n\nIf you did not ask for this, ignore this email.\n\n{{company_name}}',
            { user_name: user.name, reset_link: link, company_name: company.name },
          ),
          templateName: 'password_reset',
        });
      }
      // Always the same answer, so the endpoint cannot be used to discover accounts.
      res.json({ ok: true });
    }),
  );

  async function consumeToken(type: 'reset' | 'invite', token: string) {
    const row = await db.userToken.findUnique({
      where: { tokenHash: sha256(token) },
      include: { user: true },
    });
    if (!row || row.type !== type || row.usedAt || row.expiresAt <= new Date()) {
      throw new HttpError(400, 'INVALID_TOKEN', 'This link is invalid or has expired');
    }
    // Single use: only one concurrent request can flip usedAt.
    const claimed = await db.userToken.updateMany({
      where: { id: row.id, usedAt: null },
      data: { usedAt: new Date() },
    });
    if (claimed.count !== 1)
      throw new HttpError(400, 'INVALID_TOKEN', 'This link is invalid or has expired');
    return row.user;
  }

  router.post(
    '/password/reset',
    resetLimiter,
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({ token: z.string().min(20).max(200), password: passwordSchema }),
        req.body,
      );
      const probe = await db.userToken.findUnique({
        where: { tokenHash: sha256(body.token) },
        include: { user: true },
      });
      if (probe)
        assertStrongPassword(body.password, { email: probe.user.email, name: probe.user.name });
      const user = await consumeToken('reset', body.token);
      await db.user.update({
        where: { id: user.id },
        data: {
          passwordHash: await hashPassword(body.password),
          failedLogins: 0,
          lockedUntil: null,
        },
      });
      await revokeAllSessions(db, user.id);
      await audit(
        db,
        { companyId: user.companyId, userId: user.id, ip: clientIp(req) },
        {
          action: 'auth.password_reset',
          entity: 'user',
          entityId: user.id,
        },
      );
      res.json({ ok: true });
    }),
  );

  router.post(
    '/invitations/accept',
    resetLimiter,
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          token: z.string().min(20).max(200),
          password: passwordSchema,
          name: z.string().trim().min(1).max(200).optional(),
        }),
        req.body,
      );
      const probe = await db.userToken.findUnique({
        where: { tokenHash: sha256(body.token) },
        include: { user: true },
      });
      if (probe)
        assertStrongPassword(body.password, {
          email: probe.user.email,
          name: body.name ?? probe.user.name,
        });
      const user = await consumeToken('invite', body.token);
      await db.user.update({
        where: { id: user.id },
        data: {
          passwordHash: await hashPassword(body.password),
          status: 'active',
          ...(body.name ? { name: body.name } : {}),
        },
      });
      await audit(
        db,
        { companyId: user.companyId, userId: user.id, ip: clientIp(req) },
        {
          action: 'user.invitation_accepted',
          entity: 'user',
          entityId: user.id,
        },
      );
      res.json({ ok: true });
    }),
  );

  return router;
}

export { INVITE_TTL_MS };
